import { useState, useRef } from 'react';
import { Upload, Send, CheckCircle, AlertCircle, X } from 'lucide-react';

interface CandidatureFormProps {
  missionTitle: string;
  offerId?: string;
  offerSlug?: string;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  linkedinUrl: string;
  message: string;
  rgpdConsent: boolean;
  website: string; // honeypot
}

const EMPTY: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  linkedinUrl: '',
  message: '',
  rgpdConsent: false,
  website: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s.\-()]{8,}$/;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip data URL prefix: "data:application/pdf;base64,"
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function CandidatureForm({
  missionTitle,
  offerId,
  offerSlug,
}: CandidatureFormProps) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState | 'cv' | 'global', string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'success_warning' | 'error'>('idle');
  const fileInputRef = useRef<HTMLInputElement>(null);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    if (!file) { setCvFile(null); return; }

    if (file.type !== 'application/pdf') {
      setErrors((prev) => ({ ...prev, cv: 'Seuls les fichiers PDF sont acceptés.' }));
      setCvFile(null);
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, cv: 'Le fichier ne doit pas dépasser 5 Mo.' }));
      setCvFile(null);
      e.target.value = '';
      return;
    }

    setCvFile(file);
    setErrors((prev) => ({ ...prev, cv: undefined }));
  }

  function validate(): boolean {
    const next: typeof errors = {};

    if (!form.firstName.trim()) next.firstName = 'Le prénom est obligatoire.';
    if (!form.lastName.trim())  next.lastName  = 'Le nom est obligatoire.';
    if (!form.email.trim() || !EMAIL_RE.test(form.email)) next.email = 'Adresse email invalide.';
    if (!form.phone.trim() || !PHONE_RE.test(form.phone)) next.phone = 'Numéro de téléphone invalide (10 chiffres minimum).';
    if (!cvFile) next.cv = 'Le CV est obligatoire.';
    if (!form.rgpdConsent) next.rgpdConsent = 'Vous devez accepter la politique de confidentialité.';

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate() || submitting) return;

    setSubmitting(true);
    setErrors({});

    try {
      const base64 = await fileToBase64(cvFile!);

      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        linkedinUrl: form.linkedinUrl.trim() || undefined,
        message: form.message.trim() || undefined,
        missionTitle,
        offerId: offerId || undefined,
        offerSlug: offerSlug || undefined,
        rgpdConsent: form.rgpdConsent,
        website: form.website,
        cv: {
          filename: cvFile!.name,
          contentType: 'application/pdf',
          base64,
        },
      };

      const res = await fetch('/.netlify/functions/candidature', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setErrors({ global: data.error ?? 'Une erreur est survenue. Veuillez réessayer.' });
        setStatus('error');
        return;
      }

      if (data.warning === 'CV à renvoyer') {
        setStatus('success_warning');
      } else {
        setStatus('success');
      }

      setForm(EMPTY);
      setCvFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      setErrors({ global: 'Une erreur réseau est survenue. Veuillez réessayer.' });
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Success states ──
  if (status === 'success') {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
        <CheckCircle className="mx-auto h-10 w-10 text-emerald-500" />
        <h3 className="mt-4 text-lg font-bold text-emerald-900">Candidature envoyée avec succès !</h3>
        <p className="mt-2 text-sm text-emerald-700">
          Nous reviendrons vers vous sous 48h.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="mt-6 inline-flex items-center gap-2 rounded-xl border border-emerald-300 px-5 py-2.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
        >
          Envoyer une autre candidature
        </button>
      </div>
    );
  }

  if (status === 'success_warning') {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center">
        <CheckCircle className="mx-auto h-10 w-10 text-amber-500" />
        <h3 className="mt-4 text-lg font-bold text-amber-900">Candidature enregistrée</h3>
        <p className="mt-2 text-sm text-amber-700">
          Candidature enregistrée, mais le CV n'a pas pu être joint. Notre équipe vous recontactera pour le récupérer.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="mt-6 inline-flex items-center gap-2 rounded-xl border border-amber-300 px-5 py-2.5 text-sm font-medium text-amber-700 hover:bg-amber-100 transition-colors"
        >
          Envoyer une autre candidature
        </button>
      </div>
    );
  }

  // ── Form ──
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
      <h2 className="text-xl font-bold text-slate-900">
        Postuler à : <span className="text-pink-600">{missionTitle}</span>
      </h2>
      <p className="mt-1 text-sm text-slate-500">
        Les champs marqués <span className="text-red-500">*</span> sont obligatoires.
      </p>

      {errors.global && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <p className="text-sm text-red-700">{errors.global}</p>
          <button
            onClick={() => { setErrors({}); setStatus('idle'); }}
            className="ml-auto shrink-0 text-red-400 hover:text-red-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Honeypot — hidden from real users */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={(e) => set('website', e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        style={{ display: 'none' }}
        aria-hidden="true"
      />

      <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-5">

        {/* Name row */}
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Prénom" required error={errors.firstName}>
            <input
              type="text"
              value={form.firstName}
              onChange={(e) => set('firstName', e.target.value)}
              placeholder="Marie"
              className={inputCls(!!errors.firstName)}
              autoComplete="given-name"
            />
          </FormField>
          <FormField label="Nom" required error={errors.lastName}>
            <input
              type="text"
              value={form.lastName}
              onChange={(e) => set('lastName', e.target.value)}
              placeholder="Dupont"
              className={inputCls(!!errors.lastName)}
              autoComplete="family-name"
            />
          </FormField>
        </div>

        {/* Contact row */}
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Email" required error={errors.email}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder="marie.dupont@email.com"
              className={inputCls(!!errors.email)}
              autoComplete="email"
            />
          </FormField>
          <FormField label="Téléphone" required error={errors.phone}>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              placeholder="06 12 34 56 78"
              className={inputCls(!!errors.phone)}
              autoComplete="tel"
            />
          </FormField>
        </div>

        {/* LinkedIn */}
        <FormField label="URL LinkedIn" error={errors.linkedinUrl}>
          <input
            type="url"
            value={form.linkedinUrl}
            onChange={(e) => set('linkedinUrl', e.target.value)}
            placeholder="https://linkedin.com/in/marie-dupont"
            className={inputCls(!!errors.linkedinUrl)}
            autoComplete="url"
          />
        </FormField>

        {/* CV upload */}
        <FormField label="CV (PDF)" required error={errors.cv}>
          <div
            className={`relative flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-8 transition-colors ${
              errors.cv
                ? 'border-red-300 bg-red-50'
                : cvFile
                ? 'border-emerald-300 bg-emerald-50'
                : 'border-slate-300 bg-slate-50 hover:border-pink-400 hover:bg-pink-50'
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            {cvFile ? (
              <>
                <CheckCircle className="h-8 w-8 text-emerald-500" />
                <p className="mt-2 text-sm font-medium text-emerald-700">{cvFile.name}</p>
                <p className="text-xs text-emerald-500">{(cvFile.size / 1024).toFixed(0)} Ko</p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCvFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="mt-2 text-xs text-slate-400 underline hover:text-red-500"
                >
                  Supprimer
                </button>
              </>
            ) : (
              <>
                <Upload className="h-8 w-8 text-slate-400" />
                <p className="mt-2 text-sm font-medium text-slate-700">
                  Cliquez pour sélectionner votre CV
                </p>
                <p className="text-xs text-slate-400">PDF uniquement · 5 Mo max</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              className="sr-only"
            />
          </div>
        </FormField>

        {/* Message */}
        <FormField label="Message / Motivation" error={errors.message}>
          <textarea
            value={form.message}
            onChange={(e) => set('message', e.target.value)}
            rows={4}
            placeholder="Parlez-nous de votre parcours et de vos motivations..."
            className={`${inputCls(!!errors.message)} resize-y`}
          />
        </FormField>

        {/* RGPD */}
        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.rgpdConsent}
              onChange={(e) => set('rgpdConsent', e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-pink-600 focus:ring-pink-500"
            />
            <span className="text-sm text-slate-600 leading-relaxed">
              <span className="text-red-500">* </span>
              J'accepte que mes données soient traitées par Recruteurs.io dans le cadre de cette
              candidature, conformément à la{' '}
              <a
                href="/legal/confidentialite"
                target="_blank"
                rel="noopener noreferrer"
                className="text-pink-600 underline hover:text-pink-700"
              >
                politique de confidentialité
              </a>
              .
            </span>
          </label>
          {errors.rgpdConsent && (
            <p className="mt-1.5 text-xs text-red-500">{errors.rgpdConsent}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-pink-600 px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-pink-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Envoi en cours…
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              Envoyer ma candidature
            </>
          )}
        </button>
      </form>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function FormField({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="ml-0.5 text-red-500"> *</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

function inputCls(hasError: boolean) {
  return `w-full rounded-xl border px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:ring-1 ${
    hasError
      ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200'
      : 'border-slate-300 bg-white focus:border-pink-500 focus:ring-pink-200'
  }`;
}
